/**
 * Centralized authentication state management
 * 
 * This utility provides a single source of truth for authentication state
 * and ensures all components have consistent access to auth information.
 */

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  companyId?: string;
  consultingFirmId?: string;
}

export interface AuthCompany {
  id: string;
  name: string;
  subdomain?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: AuthUser | null;
  company: AuthCompany | null;
}

// Event name for auth state changes
export const AUTH_CHANGE_EVENT = 'pathkit_auth_change';

/**
 * Get current authentication state from localStorage
 */
export function getAuthState(): AuthState {
  try {
    // Check for both session ID (new format) and access token (old format)
    const sessionId = localStorage.getItem('pathkit_session_id');
    const accessToken = localStorage.getItem('pathkit_access_token');
    const token = sessionId || accessToken;
    
    const userStr = localStorage.getItem('pathkit_user');
    const companyStr = localStorage.getItem('pathkit_company');

    // DEBUG: Log what we found
    console.log('[authState] getAuthState called, found:', {
      hasSessionId: !!sessionId,
      hasAccessToken: !!accessToken,
      hasUser: !!userStr,
      hasCompany: !!companyStr,
      allKeys: Object.keys(localStorage)
    });

    if (!token || !userStr) {
      console.log('[authState] Returning unauthenticated - missing token or user');
      return {
        isAuthenticated: false,
        accessToken: null,
        user: null,
        company: null,
      };
    }

    const user = JSON.parse(userStr);
    const company = companyStr ? JSON.parse(companyStr) : null;

    console.log('[authState] Returning authenticated state for:', user.email);
    return {
      isAuthenticated: true,
      accessToken: token, // Use either session ID or access token
      user,
      company,
    };
  } catch (error) {
    console.error('[authState] Error reading auth state:', error);
    return {
      isAuthenticated: false,
      accessToken: null,
      user: null,
      company: null,
    };
  }
}

/**
 * Save authentication state to localStorage and notify listeners
 */
export function setAuthState(
  isAuthenticated: boolean,
  user: AuthUser | null,
  company: AuthCompany | null = null
): void {
  try {
    if (!isAuthenticated || !user) {
      // Clear auth state if not authenticated
      clearAuthState();
      return;
    }
    
    // Note: We don't store accessToken here anymore since it's stored separately
    // as either pathkit_session_id (new format) or pathkit_access_token (old format)
    // by the login/session management code
    
    localStorage.setItem('pathkit_user', JSON.stringify(user));
    if (company) {
      localStorage.setItem('pathkit_company', JSON.stringify(company));
    }

    // Notify all listeners
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
    
    console.log('[authState] Auth state saved:', {
      userId: user.id,
      email: user.email,
      role: user.role,
      hasCompany: !!company,
    });
  } catch (error) {
    console.error('[authState] Error saving auth state:', error);
  }
}

/**
 * Clear authentication state and notify listeners
 */
export function clearAuthState(): void {
  try {
    console.log('[authState] clearAuthState called - STACK TRACE:');
    console.trace();
    
    // Clear all auth-related items from localStorage
    localStorage.removeItem('pathkit_access_token');
    localStorage.removeItem('pathkit_session_id');
    localStorage.removeItem('pathkit_user');
    localStorage.removeItem('pathkit_company');

    // Notify all listeners
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
    
    console.log('[authState] Auth state cleared');
  } catch (error) {
    console.error('[authState] Error clearing auth state:', error);
  }
}

/**
 * Subscribe to authentication state changes
 * Returns an unsubscribe function
 */
export function subscribeToAuthChanges(callback: () => void): () => void {
  const handler = () => {
    callback();
  };

  // Listen for custom auth events
  window.addEventListener(AUTH_CHANGE_EVENT, handler);

  // Listen for storage events from other tabs
  const storageHandler = (e: StorageEvent) => {
    if (
      e.key === 'pathkit_access_token' ||
      e.key === 'pathkit_session_id' ||
      e.key === 'pathkit_user' ||
      e.key === 'pathkit_company'
    ) {
      callback();
    }
  };
  window.addEventListener('storage', storageHandler);

  // Return unsubscribe function
  return () => {
    window.removeEventListener(AUTH_CHANGE_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
}

/**
 * Check if current user has a specific role
 */
export function hasRole(role: string | string[]): boolean {
  const { user } = getAuthState();
  if (!user) return false;

  const roles = Array.isArray(role) ? role : [role];
  return roles.includes(user.role);
}

/**
 * Check if current user is a system admin
 */
export function isSystemAdmin(): boolean {
  return hasRole(['systemAdmin', 'system_admin']);
}

/**
 * Check if current user is a consultant
 */
export function isConsultant(): boolean {
  return hasRole(['consultant', 'cfAdmin', 'billingAdmin', 'bdRep']);
}

/**
 * Check if current user is a client company user
 */
export function isClientUser(): boolean {
  return hasRole(['companyAdmin', 'user']);
}

import { getAuthHeaders as buildAuthHeaders } from './apiHeaders';

/**
 * Get authorization headers for API requests.
 * Uses the canonical getAuthHeaders() which sends the anon key in
 * Authorization (for the Supabase gateway) and the session token in
 * X-Session-Token (for app-level auth).
 */
export function getAuthHeader(): Record<string, string> {
  const { accessToken } = getAuthState();
  if (!accessToken) {
    return buildAuthHeaders();
  }
  return buildAuthHeaders(accessToken);
}